import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { buildSeoTitle } from "@/lib/seoHelpers";
import { getSearchDescription } from "@/lib/searchDescriptions";

export type ArticleWithAuthor = Tables<"articles"> & {
  authors?: {
    name: string;
    slug: string;
    role?: string | null;
    bio?: string | null;
    avatar_url?: string | null;
  } | null;
};

export const ARTICLE_SLUG_REDIRECTS: Record<string, string> = {
  "review-of-hy3750i-hyundai-inverter-framed-generator-2026":
    "hyundai-hy3750i-inverter-generator-review-2026",
  // Legacy punctuated slugs normalised during the 2026 content clean-up.
  // (Pure case changes are already resolved by the case-insensitive lookup below.)
  "choosing-gardening-tools:-digging-&-cultivating-essentials":
    "choosing-gardening-tools-digging-and-cultivating-essentials",
  "hyundai-hyc40li-cordless-battery-chainsaw-review-is-the-40v-14-inch-saw-worth-£199":
    "hyundai-hyc40li-cordless-battery-chainsaw-review-is-the-40v-14-inch-saw-worth-199",
  "how-to-create-a-beautiful,-low-maintenance-moss-lawn-in-the-uk":
    "how-to-create-a-beautiful-low-maintenance-moss-lawn-in-the-uk",
  "the-ultimate-guide-to-removing-tree-stumps:-8-proven-methods-for-every-gardener":
    "the-ultimate-guide-to-removing-tree-stumps-8-proven-methods-for-every-gardener",
  "uk-spring-lawn-care-guide:-from-mossy-patchwork-to-emerald-green":
    "uk-spring-lawn-care-guide-from-mossy-patchwork-to-emerald-green",
};

export const normalizeArticleSlug = (value: string) =>
  decodeURIComponent(value)
    .trim()
    .replace(/\/+$/, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export const getArticleSlugKey = (value: string) => normalizeArticleSlug(value).toLowerCase();

export const getArticleRedirectTarget = (value: string) => {
  const normalized = normalizeArticleSlug(value);
  return ARTICLE_SLUG_REDIRECTS[getArticleSlugKey(value)] || (normalized !== value ? normalized : null);
};

export const getLikelyArticleIdentifier = (value: string) =>
  getArticleSlugKey(value)
    .split("-")
    .find((part) => /[a-z]/.test(part) && /\d/.test(part) && part.length >= 4);

const SELECT = "*, authors(name, slug, role, bio, avatar_url)";

export async function fetchArticleBySlug(slug: string): Promise<ArticleWithAuthor | null> {
  const slugCandidates = Array.from(
    new Set([slug, normalizeArticleSlug(slug), encodeURIComponent(slug)].filter(Boolean)),
  );

  for (const candidate of slugCandidates) {
    const { data, error } = await supabase
      .from("articles")
      .select(SELECT)
      .eq("slug", candidate)
      .eq("status", "published")
      .maybeSingle();

    if (error) throw error;
    if (data) return data as ArticleWithAuthor;
  }

  const normalizedSlug = normalizeArticleSlug(slug);
  const { data: caseInsensitiveMatches, error: caseInsensitiveError } = await supabase
    .from("articles")
    .select(SELECT)
    .ilike("slug", normalizedSlug)
    .eq("status", "published")
    .limit(2);

  if (caseInsensitiveError) throw caseInsensitiveError;
  if (caseInsensitiveMatches?.length === 1) return caseInsensitiveMatches[0] as ArticleWithAuthor;

  const likelyIdentifier = getLikelyArticleIdentifier(slug);
  if (likelyIdentifier) {
    const { data, error } = await supabase
      .from("articles")
      .select(SELECT)
      .ilike("slug", `%${likelyIdentifier}%`)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(2);

    if (error) throw error;
    if (data?.length === 1) return data[0] as ArticleWithAuthor;
  }

  return null;
}

export const articleQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["article", slug],
    queryFn: () => fetchArticleBySlug(slug),
  });

const SITE_URL = "https://www.garden-review.co.uk";

/** Plant grow guides are canonically served from /plants/{slug}. */
export const PLANT_GUIDE_TAG = "Plant Guide";

export const isPlantGuide = (article: { tags?: string[] | null } | null | undefined) =>
  Array.isArray(article?.tags) && article.tags.includes(PLANT_GUIDE_TAG);

/** Canonical public path for an article. /articles/:slug is the single canonical (/plants/:slug 301s here). */
export const getArticlePath = (article: Pick<ArticleWithAuthor, "slug">) => `/articles/${article.slug}`;

/** Decode HTML entities stored in DB text so head tags are encoded exactly once. */
export function decodeEntities(text: string): string {
  return text
    .replace(/&(amp|#0*38|#x0*26);/gi, "&")
    .replace(/&(quot|#0*34);/gi, '"')
    .replace(/&(#0*39|apos|rsquo|lsquo);/gi, "'")
    .replace(/&nbsp;/gi, " ")
    .replace(/&(lt);/gi, "<")
    .replace(/&(gt);/gi, ">")
    .replace(/&(ndash);/gi, "–")
    .replace(/&(mdash);/gi, "—")
    .replace(/&(pound);/gi, "£");
}

/** Text that looks like a byline/date block rather than a summary. */
const BYLINE_RE = /(📅|✍|Reviewed:|Last tested|Last updated|^\s*by\s|^\s*[A-Z]{1,3}\s+[A-Z][a-z]+\s+[A-Z][a-z]+\s+[-–—]|\b\d+\+?\s+years?\s+(testing|experience)\b|\s·\s[^·]+\s·\s)/i;

const stripHtml = (html: string) => decodeEntities(html.replace(/<[^>]*>/g, " ")).replace(/[#*_`>|]/g, " ").replace(/\s+/g, " ").trim();

const clip = (text: string, max = 160) => {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const i = cut.lastIndexOf(" ");
  return `${(i > max * 0.6 ? cut.slice(0, i) : cut).replace(/[\s,;:–—-]+$/, "")}.`;
};

/** Meta description: stored value, else excerpt, else first real body paragraph — never a byline block. */
export function buildArticleDescription(article: ArticleWithAuthor): string {
  const record = article as unknown as Record<string, string | null | undefined>;
  const candidates = [record.meta_description, article.excerpt];
  for (const c of candidates) {
    const t = c ? stripHtml(c) : "";
    if (t && !BYLINE_RE.test(t)) return getSearchDescription(`/articles/${article.slug}`, clip(t));
  }
  const paragraphs = (article.content || "")
    .split(/<\/p>|\n\s*\n/i)
    .map(stripHtml)
    .filter((p) => p.length >= 60 && !BYLINE_RE.test(p));
  if (paragraphs[0]) return getSearchDescription(`/articles/${article.slug}`, clip(paragraphs[0]));
  return getSearchDescription(`/articles/${article.slug}`, `Read ${decodeEntities(article.title)} on Garden-Review.`);
}

/** Head metadata for an article route, server-rendered so crawlers see it. */
export function buildArticleHead(article: ArticleWithAuthor | null | undefined) {
  if (!article) {
    return {
      meta: [
        { title: "Article Not Found | Garden-Review" },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
  }

  const record = article as unknown as Record<string, string | null | undefined>;
  const articleTitle = decodeEntities(article.title);
  // Stored meta titles that were cut off with an ellipsis are ignored in favour of the full title.
  const storedMeta = record.meta_title ? decodeEntities(record.meta_title).trim() : "";
  const seoTitle = storedMeta && !/(…|\.\.\.)$/.test(storedMeta) ? storedMeta : articleTitle;
  const description = buildArticleDescription(article);
  const url = `${SITE_URL}${getArticlePath(article)}`;
  const authorName = article.authors?.name || article.author || "Garden-Review";

  const image = article.featured_image;
  const imageAlt = article.featured_image_alt || article.title;

  const meta: Array<Record<string, string>> = [
    { title: buildSeoTitle(seoTitle) },
    { name: "description", content: description },
    { name: "author", content: authorName },
    { property: "og:type", content: "article" },
    { property: "og:title", content: articleTitle },
    { property: "og:description", content: description },
    { name: "twitter:title", content: articleTitle },
    { name: "twitter:description", content: description },
  ];

  if (!image) meta.push({ name: "twitter:card", content: "summary" });
  if (image) {
    meta.push(
      { property: "og:image", content: image },
      { property: "og:image:alt", content: imageAlt },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: imageAlt },
    );
  }

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
  };
}
